import React from 'react';
import WaveDivider from '../home/WaveDivider';

export const SectionBadge: React.FC<{ label: string; className?: string }> = ({ label, className = '' }) => (
    <span
        className={`inline-block px-4 py-1 rounded-full bg-princonser-purple-muted text-princonser-purple text-sm font-semibold ${className}`}
    >
        {label}
    </span>
);

export const Doodles: React.FC = () => (
    <>
        <span className="absolute top-10 left-6 sm:left-12 text-3xl opacity-25 pointer-events-none" aria-hidden>✈️</span>
        <span className="absolute top-16 right-8 sm:right-16 text-2xl opacity-25 pointer-events-none" aria-hidden>⭐</span>
        <span className="absolute bottom-20 left-10 text-xl opacity-20 pointer-events-none" aria-hidden>✦</span>
        <span className="absolute top-1/3 right-6 text-4xl opacity-20 pointer-events-none" aria-hidden>🌈</span>
    </>
);

export const ElementHeader: React.FC<{
    element: string;
    title: string;
    subtitle?: string;
    description?: string;
    centered?: boolean;
}> = ({ element, title, subtitle, description, centered = true }) => (
    <div className={`mb-10 sm:mb-12 ${centered ? 'text-center' : ''}`}>
        <SectionBadge label={element} className="mb-4" />
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-princonser-navy mb-3">{title}</h2>
        {subtitle && (
            <p className="text-princonser-purple font-medium text-base sm:text-lg mb-3 max-w-3xl mx-auto">{subtitle}</p>
        )}
        {description && (
            <p className="text-princonser-text-body text-sm sm:text-base max-w-3xl mx-auto leading-relaxed">{description}</p>
        )}
        {subtitle && <div className="w-16 h-1 bg-princonser-purple rounded-full mx-auto mt-4" />}
    </div>
);

export const PageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="min-h-screen bg-white overflow-x-hidden">{children}</div>
);

export const IntroWave: React.FC = () => (
    <div className="bg-princonser-hero pt-16">
        <WaveDivider fillClass="text-white" />
    </div>
);

interface HealthBlockProps {
    id: string;
    title: string;
    description: string;
    image: string;
    theme: 'purple' | 'orange' | 'green' | 'gold';
    features: { icon: string; label: string }[];
    imageRight?: boolean;
}

const healthThemes = {
    purple: {
        bg: 'bg-princonser-purple-muted',
        title: 'text-princonser-purple',
        card: 'bg-white border-princonser-purple-muted',
        feature: 'text-princonser-purple',
    },
    orange: {
        bg: 'bg-princonser-orange-soft',
        title: 'text-princonser-orange-dark',
        card: 'bg-white border-orange-100',
        feature: 'text-princonser-orange-dark',
    },
    green: {
        bg: 'bg-emerald-50',
        title: 'text-emerald-700',
        card: 'bg-white border-emerald-100',
        feature: 'text-emerald-700',
    },
    gold: {
        bg: 'bg-amber-50',
        title: 'text-amber-700',
        card: 'bg-white border-amber-100',
        feature: 'text-amber-700',
    },
};

export const HealthBlock: React.FC<HealthBlockProps> = ({
    id,
    title,
    description,
    image,
    theme,
    features,
    imageRight = false,
}) => {
    const c = healthThemes[theme];
    return (
        <div id={id} className={`scroll-mt-24 rounded-3xl ${c.bg} p-6 sm:p-8 lg:p-10`}>
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${imageRight ? 'lg:flex-row-reverse' : ''}`}>
                <div className={imageRight ? 'lg:order-2' : ''}>
                    <div className="relative rounded-2xl overflow-hidden bg-white/70 shadow-inner aspect-[4/3] max-h-80">
                        <img src={image} alt={title} className="w-full h-full object-contain p-4" />
                    </div>
                </div>
                <div className={imageRight ? 'lg:order-1' : ''}>
                    <h3 className={`text-2xl sm:text-3xl font-bold mb-4 ${c.title}`}>{title}</h3>
                    <p className="text-princonser-text-body text-sm sm:text-base leading-relaxed mb-6">{description}</p>
                    <div className="grid grid-cols-3 gap-3">
                        {features.map((f) => (
                            <div key={f.label} className={`${c.card} border rounded-xl p-3 text-center shadow-sm`}>
                                <div className="text-2xl mb-1">{f.icon}</div>
                                <p className={`text-xs font-bold ${c.feature}`}>{f.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
